import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MessageCircle, X, Send, Sparkles, ShoppingBag, Gift, Star, Shirt } from 'lucide-react';
import { Button } from './ui/button';
import { Link, useLocation } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Quick action message mapping - extracted for clarity
const QUICK_ACTION_MESSAGES = {
  'new_arrivals': "Show me your newest arrivals!",
  'gift_finder': "I'm looking for a faith-inspired gift",
  'sales': "What's on sale right now?",
  'style_tips': "Give me some style inspiration!"
};

// Quick action icons - extracted to reduce complexity
const QUICK_ACTION_ICONS = {
  'new_arrivals': Star,
  'gift_finder': Gift,
  'sales': ShoppingBag,
  'style_tips': Shirt
};

// A.I_SHA Avatar Component
export const AishaAvatar = ({ size = 'md' }) => {
  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12'
  };
  
  return (
    <div className={`${sizeClasses[size]} rounded-full bg-gradient-to-br from-amber-400 via-pink-500 to-purple-600 flex items-center justify-center shadow-lg`}>
      <Sparkles className="w-1/2 h-1/2 text-white" />
    </div>
  );
};

// Product Card in Chat
const ChatProductCard = ({ product }) => (
  <Link 
    to={`/products/${product.id || product.name?.toLowerCase().replace(/\s+/g, '-')}`}
    className="block bg-white/10 backdrop-blur rounded-lg p-2 hover:bg-white/20 transition-all"
  >
    <div className="flex gap-2">
      {product.images?.[0] && (
        <img 
          src={product.images[0]} 
          alt={product.name}
          className="w-12 h-12 object-cover rounded"
        />
      )}
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-white truncate">{product.name}</p>
        <p className="text-xs text-amber-300">${product.price?.toFixed(2)}</p>
      </div>
    </div>
  </Link>
);

const AishaChatWidget = () => {
  const { pathname } = useLocation();
  if (['/voyage', '/', '/selah'].includes(pathname)) return null;
  return <AishaChatWidgetInner />;
};

const AishaChatWidgetInner = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [greeting, setGreeting] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  
  // Fetch greeting on first open
  useEffect(() => {
    if (isOpen && !greeting) {
      fetchGreeting();
    }
  }, [isOpen, greeting]);
  
  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  const fetchGreeting = async () => {
    try {
      const response = await fetch(`${API_URL}/api/aisha-public/greeting`);
      const data = await response.json();
      setGreeting(data);
      
      // Add greeting as first message
      setMessages([{
        type: 'aisha',
        content: `${data.greeting} ${data.message}`,
        tagline: data.tagline,
        quickActions: data.quick_actions
      }]);
    } catch (error) {
      console.error('Failed to fetch greeting:', error);
      setMessages([{
        type: 'aisha',
        content: "Welcome to ALL ABOARD SELAH! 💛",
        tagline: "I'm A.I_SHA, your faith-inspired shopping companion. How can I help you today?"
      }]);
    }
  };
  
  const sendMessage = useCallback(async (text = input) => {
    if (!text.trim()) return;
    
    // Add user message
    setMessages(prev => [...prev, { type: 'user', content: text }]);
    setInput('');
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/aisha-public/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          message: text,
          session_id: sessionId
        })
      });
      
      const data = await response.json();
      setSessionId(data.session_id);
      
      // Add A.I_SHA response
      setMessages(prev => [...prev, {
        type: 'aisha',
        content: data.response,
        products: data.products
      }]);
    } catch (error) {
      console.error('Chat failed:', error);
      setMessages(prev => [...prev, {
        type: 'aisha',
        content: "I'm having a moment! Let me gather my thoughts... Try asking again? 🙏"
      }]);
    }
    
    setLoading(false);
  }, [input, sessionId]);
  
  const handleQuickAction = useCallback((action) => {
    const message = QUICK_ACTION_MESSAGES[action] || action;
    sendMessage(message);
  }, [sendMessage]);
  
  return (
    <>
      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 right-6 z-50 p-4 rounded-full bg-gradient-to-r from-amber-500 via-pink-500 to-purple-600 shadow-2xl hover:shadow-amber-500/50 transition-all duration-300 ${isOpen ? 'scale-0' : 'scale-100'}`}
        data-testid="aisha-chat-button"
      >
        <div className="relative">
          <MessageCircle className="w-6 h-6 text-white" />
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full animate-pulse" />
        </div>
      </button>
      
      {/* Chat Window */}
      <div 
        className={`fixed bottom-6 right-6 z-50 w-96 max-w-[calc(100vw-3rem)] bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 rounded-2xl shadow-2xl border border-purple-500/30 overflow-hidden transition-all duration-300 ${isOpen ? 'scale-100 opacity-100' : 'scale-0 opacity-0'}`}
        data-testid="aisha-chat-window"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500/20 via-pink-500/20 to-purple-500/20 p-4 flex items-center justify-between border-b border-purple-500/30">
          <div className="flex items-center gap-3">
            <AishaAvatar size="md" />
            <div>
              <h3 className="font-semibold text-white">A.I_SHA</h3>
              <p className="text-xs text-purple-300">Your Shopping Companion</p>
            </div>
          </div>
          <button 
            onClick={() => setIsOpen(false)}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>
        
        {/* Messages */}
        <div className="h-80 overflow-y-auto p-4 space-y-4">
          {messages.map((msg, msgIndex) => (
            <div key={`msg-${msgIndex}-${msg.type}`} className={`flex gap-2 ${msg.type === 'user' ? 'flex-row-reverse' : ''}`}>
              {msg.type === 'aisha' && <AishaAvatar size="sm" />}
              
              <div className={`max-w-[80%] ${msg.type === 'user' ? 'bg-purple-600' : 'bg-white/10'} rounded-2xl p-3`}>
                <p className="text-sm text-white">{msg.content}</p>
                
                {msg.tagline && (
                  <p className="text-xs text-purple-300 mt-1">{msg.tagline}</p>
                )}
                
                {/* Quick Actions */}
                {msg.quickActions && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {msg.quickActions.map((action) => (
                      <button
                        key={action.action}
                        onClick={() => handleQuickAction(action.action)}
                        className="text-xs bg-purple-500/30 hover:bg-purple-500/50 text-purple-200 px-3 py-1 rounded-full transition-colors"
                      >
                        {action.label}
                      </button>
                    ))}
                  </div>
                )}
                
                {/* Products */}
                {msg.products && msg.products.length > 0 && (
                  <div className="mt-3 space-y-2">
                    <p className="text-xs text-amber-400">Here are some picks for you:</p>
                    {msg.products.slice(0, 3).map((product) => (
                      <ChatProductCard key={product.id || product.name} product={product} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          
          {loading && (
            <div className="flex gap-2">
              <AishaAvatar size="sm" />
              <div className="bg-white/10 rounded-2xl p-3">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
        
        {/* Input */}
        <div className="p-4 border-t border-purple-500/30">
          <form 
            onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
            className="flex gap-2"
          >
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask me anything..."
              className="flex-1 bg-white/10 border border-purple-500/30 rounded-full px-4 py-2 text-sm text-white placeholder-gray-400 focus:outline-none focus:border-purple-400"
              data-testid="aisha-chat-input"
            />
            <Button 
              type="submit"
              disabled={!input.trim() || loading}
              className="rounded-full bg-gradient-to-r from-amber-500 to-pink-500 hover:from-amber-600 hover:to-pink-600 px-4"
              data-testid="aisha-chat-send"
            >
              <Send className="w-4 h-4" />
            </Button>
          </form>
        </div>
      </div>
    </>
  );
};

export default AishaChatWidget;
